import { Modal, ModalContent, Spinner, useDisclosure } from '@nextui-org/react';
import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

import ModelSelectArtwork from '@/app/collection/[id]/content/List/ModelSelectArtwork';
import { IconPlusWhite } from '@/assets';
import { Buttons } from '@/components';
import ClientOnly from '@/components/client-only';
import ListBarCollectionDetail from '@/components/list-bar/ListBarCollectionDetail';
import NoData from '@/components/NoData';
import RenderWhen from '@/components/RenderWhen';
import { TokenId, useTokenPrices } from '@/hooks/useTokenPrices';
import { useAuth } from '@/provider/UserProvider';
import type { CollectionData } from '@/types/collection';
import { formatNumberSocial, formattedDate, getUsdWithToken } from '@/utils';
import { currencyFormatter } from '@/utils/currency';

import Item from './Item';
import useAssetCollection from './useAssetCollection';

const ListAssetCollection: React.FC<{
  collection: CollectionData;
}> = ({ collection }) => {
  const {
    assetData,
    loading,
    currentPage,
    loadMore,
    setSearch,
    setSort,
    assetDataRef,
    setAssetData,
  } = useAssetCollection(collection.id);
  const { data: prices } = useTokenPrices();
  const { isOpen, onOpenChange, onOpen } = useDisclosure();

  const airiToUsd = useMemo(
    () =>
      getUsdWithToken({
        amount: collection.tradingVolume,
        tokenId: TokenId.AIRI,
        prices: prices || {},
      }),
    [prices, collection.tradingVolume],
  );

  const { auth } = useAuth();
  const isOwner = collection.accountAddress === auth.user.publicAddress;

  return (
    <div className="mb-10 mt-5">
      <Modal
        placement="center"
        className="max-h-[80%] w-[1255px] max-w-[80%]"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <ModelSelectArtwork
              setAssetData={setAssetData}
              assetDataRef={assetDataRef}
              assetDataIds={assetData.map((asset) => asset.id) ?? []}
              collection={collection}
              onClose={onClose}
              defaultValue={[]}
            />
          )}
        </ModalContent>
      </Modal>
      <div className="flex flex-wrap items-center justify-between ">
        <div className="flex items-start px-3 pb-1 pt-3 sm:w-full sm:flex-wrap sm:bg-gray-50">
          <div className="mb-1 mr-8 flex flex-col lg:mr-4 sm:mb-2 sm:mr-0 sm:w-[50%]">
            <p className="text-[18px] font-semibold leading-4 text-black lg:text-[16px] sm:text-14">
              {collection.numberOfItems}
            </p>
            <span className="text-[13px] text-gray-400 lg:text-[12px] sm:text-12">Total items</span>
          </div>
          <div className="mb-1 mr-8 flex flex-col lg:mr-4 sm:mb-2 sm:mr-0 sm:w-[50%]">
            <p className="text-[18px] font-semibold leading-4 text-black lg:text-[16px] sm:text-14">
              {currencyFormatter(collection.tradingVolume, { significantDigits: 3 })} AIRI
              {currencyFormatter(airiToUsd, { significantDigits: 3 }) !== '0' && (
                <span className="ml-1 text-[13px] text-gray-400">
                  {`~ $${currencyFormatter(airiToUsd, { significantDigits: 3 })}`}
                </span>
              )}
            </p>
            <span className="text-[13px] text-gray-400 lg:text-[12px] sm:text-12">
              Total Volume
            </span>
          </div>
          <div className="mb-1 mr-8 flex flex-col lg:mr-4 sm:mb-2 sm:mr-0 sm:w-[50%]">
            <p className="text-[18px] font-semibold leading-4 text-black lg:text-[16px] sm:text-14">
              {formatNumberSocial(collection.totalView) ?? '1'}
            </p>
            <span className="text-[13px] text-gray-400 lg:text-[12px] sm:text-12">Total view</span>
          </div>
          <div className="mb-1 mr-8 flex flex-col lg:mr-4 sm:mb-2 sm:mr-0 sm:w-[50%]">
            <p className="text-[18px] font-semibold leading-4 text-black lg:text-[16px] sm:text-14">
              {formattedDate(collection.createdAt)}
            </p>
            <span className="text-[13px] text-gray-400 lg:text-[12px] sm:text-12">
              Date created
            </span>
          </div>
        </div>
      </div>
      <div className="mt-10 flex items-center justify-between sm:flex-wrap">
        <p className="text-18 font-semibold sm:hidden">{assetData?.length} Items</p>
        <div className="flex items-center justify-end md:flex-col md:items-start sm:w-full sm:flex-row">
          <div className="md:ml-[-8px] sm:ml-0 sm:w-full">
            <ListBarCollectionDetail onChangeSearch={setSearch} onChangeSort={setSort} />
          </div>
          {assetData.length > 0 && isOwner && (
            <div className="ml-3 flex items-center text-center md:ml-0 md:mt-3 sm:ml-2 sm:mt-0">
              <Buttons
                className="sm:h-[40px] sm:w-[40px] sm:min-w-0 sm:rounded-full sm:p-0"
                onClick={onOpen}
              >
                <IconPlusWhite /> <span className="sm:hidden">Add items</span>
              </Buttons>
            </div>
          )}
        </div>
      </div>
      <div className="mt-10">
        <RenderWhen>
          <RenderWhen.If isTrue={loading && assetData.length === 0}>
            <div className="flex min-h-[400px] items-center justify-center">
              <Spinner label="loading..." />
            </div>
          </RenderWhen.If>
          <RenderWhen.If isTrue={assetData.length === 0 && !loading}>
            <NoData className="!h-[300px]" />
            <ClientOnly>
              <RenderWhen.If isTrue={isOwner}>
                <div className="text-center">
                  <Buttons onClick={onOpen}>Add items to collection</Buttons>
                </div>
              </RenderWhen.If>
            </ClientOnly>
          </RenderWhen.If>
          <RenderWhen.If isTrue={assetData.length > 0}>
            <div className="mt-10">
              <InfiniteScroll
                style={{ overflow: 'hidden' }}
                dataLength={assetData?.length}
                next={() => loadMore(currentPage + 1)}
                hasMore
                loader=""
              >
                <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 5 }}>
                  <Masonry gutter="16px">
                    {assetData.map((item) => (
                      <Item
                        key={item.id}
                        collectionId={collection.id}
                        asset={item}
                        isOwner={isOwner}
                      />
                    ))}
                  </Masonry>
                </ResponsiveMasonry>
              </InfiniteScroll>
            </div>
          </RenderWhen.If>
        </RenderWhen>
      </div>
    </div>
  );
};

export default ListAssetCollection;
